import { useState, useEffect, useRef, useCallback, type RefObject } from 'react';

/**
 * Direction-aware scroll hook for FAB visibility.
 * Returns true (hidden) when scrolling down, false (visible) when scrolling up.
 * Uses rAF-gated scroll tracking with micro-movement filtering.
 */
export function useScrollOffset(scrollRef: RefObject<HTMLElement | null>, _threshold = 20) {
  const [isHidden, setIsHidden] = useState(false);
  const prevScrollY = useRef(0);
  const rafId = useRef(0);

  const handleScroll = useCallback(() => {
    if (rafId.current) return;

    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      const target = scrollRef.current;
      const currentY = target ? target.scrollTop : window.scrollY;

      // Always visible near the top - guard against iOS overscroll
      if (currentY < 15) {
        setIsHidden(false);
        prevScrollY.current = currentY;
        return;
      }

      const diff = currentY - prevScrollY.current;

      // Ignore micro-movements (rubber-band bounce)
      if (Math.abs(diff) <= 5) return;

      // Scrolling down → hide, scrolling up → show
      setIsHidden(diff > 0);
      prevScrollY.current = currentY;
    });
  }, [scrollRef]);

  useEffect(() => {
    const target = scrollRef.current || window;

    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [scrollRef, handleScroll]);

  return isHidden;
}
